/**
 * Consent management utility for GDPR/CCPA compliance
 * Manages user consent for analytics tracking
 */

const CONSENT_KEY = 'analytics-consent'

export type ConsentStatus = 'pending' | 'accepted' | 'rejected'

export interface ConsentState {
  status: ConsentStatus
  timestamp: number
}

/**
 * Get the current consent status from localStorage
 */
export function getConsentStatus(): ConsentState {
  try {
    const stored = localStorage.getItem(CONSENT_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.error('Error reading consent status:', error)
  }

  return {
    status: 'pending',
    timestamp: Date.now()
  }
}

/**
 * Set the consent status in localStorage
 */
export function setConsentStatus(status: 'accepted' | 'rejected'): void {
  const consentState: ConsentState = {
    status,
    timestamp: Date.now()
  }

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentState))
  } catch (error) {
    console.error('Error saving consent status:', error)
  }
}

/**
 * Check if user has given consent for analytics
 */
export function hasAnalyticsConsent(): boolean {
  const consent = getConsentStatus()
  return consent.status === 'accepted'
}

/**
 * Reset consent status (for testing or user preference changes)
 */
export function resetConsent(): void {
  try {
    localStorage.removeItem(CONSENT_KEY)
  } catch (error) {
    console.error('Error resetting consent:', error)
  }
}
